import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useLanguage } from '../../../context/LanguageContext';
import { reportingService } from '../../../services/api';
import useIsMobile from '../../../hooks/useIsMobile';
import MobileCard from '../../../components/common/MobileCard';
import MobileHero from '../../../components/common/MobileHero';
import {
    AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer,
    ReferenceLine,
} from 'recharts';
import {
    C, FONT, PRESETS,
    getToday, getStartOfWeek, getEndOfWeek, getStartOfMonth, getStartOfYear,
    fmt, sectionLabel,
    DownloadIcon, InfoIcon, WarningIcon, TrendUpIcon,
    EmptyState, BarListItem, SectionCard, SectionDivider,
} from './_shared';

/* ══════════════════════════════════════════════════════════════
   Cost Management — full P&L cost decomposition + data health + price variance
   ══════════════════════════════════════════════════════════════ */

export default function CostManagementPage({ parentDates } = {}) {
    const navigate = useNavigate();
    const { getCurrency } = useAuth();
    const currency = getCurrency ? getCurrency() : '';
    const { t } = useLanguage();
    const isMobile = useIsMobile();

    // Deep-link into the Products page focused on a flagged product. 'unknown'
    // rows have no recipe → drop the user on the details tab to set cost_price;
    // 'recipe_partial' rows → recipe tab so they can fix the missing ingredient.
    const openProductFix = useCallback((product) => {
        const tab = product.cost_source === 'recipe_partial' ? 'recipe' : 'details';
        navigate(`/portal/products?open=${encodeURIComponent(product.product_code)}&tab=${tab}`);
    }, [navigate]);

    const [preset, setPreset] = useState('month');
    const [customStart, setCustomStart] = useState(getToday);
    const [customEnd, setCustomEnd] = useState(getToday);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [narrative, setNarrative] = useState(null);
    const [flippedCards, setFlippedCards] = useState({});
    const [healthExpanded, setHealthExpanded] = useState(false);

    const internalRange = useMemo(() => {
        switch (preset) {
            case 'today': return { start: getToday(), end: getToday() };
            case 'week': return { start: getStartOfWeek(), end: getEndOfWeek() };
            case 'month': return { start: getStartOfMonth(), end: getToday() };
            case 'year': return { start: getStartOfYear(), end: getToday() };
            case 'custom': return { start: customStart, end: customEnd };
            default: return { start: getToday(), end: getToday() };
        }
    }, [preset, customStart, customEnd]);
    const dateRange = parentDates || internalRange;

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const result = await reportingService.getCostsV2(dateRange.start, dateRange.end);
            setData(result);
        } catch (err) {
            console.error('Costs fetch error:', err);
            setData(null);
        }
        setLoading(false);
    }, [dateRange.start, dateRange.end]);

    useEffect(() => { fetchData(); }, [fetchData]);

    useEffect(() => {
        reportingService.getCostNarrativeV2(dateRange.start, dateRange.end)
            .then(res => setNarrative(res))
            .catch(() => setNarrative(null));
    }, [dateRange.start, dateRange.end]);

    const toggleFlip = (idx) => {
        setFlippedCards(prev => ({ ...prev, [idx]: !prev[idx] }));
    };

    const fmtDate = (s) => new Date(s + 'T12:00:00').toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
    const currentLabel = `${fmtDate(dateRange.start)} – ${fmtDate(dateRange.end)}`;
    const prevLabel = data?.comparison_period
        ? `${fmtDate(data.comparison_period.start)} – ${fmtDate(data.comparison_period.end)}`
        : '';

    const kpis = data?.kpis || [];
    const sections = data?.sections || {};
    const insights = data?.insights || [];
    const flow = sections.cost_flow || {};
    const overhead = sections.expense_categories?.overhead || [];
    const daily = sections.expense_categories?.daily || [];
    const priceVariance = sections.price_variance || [];
    const dailyCosts = sections.daily_costs || [];
    const health = sections.cost_data_health || {};
    const kpiDescriptions = narrative?.kpi_descriptions || {};

    const overheadTotal = overhead.reduce((s, e) => s + Number(e.total_cost || 0), 0);
    const overheadMax = Math.max(1, ...overhead.map(e => Number(e.total_cost || 0)));
    const dailyTotal = daily.reduce((s, e) => s + Number(e.total_cost || 0), 0);
    const dailyMax = Math.max(1, ...daily.map(e => Number(e.total_cost || 0)));

    const dailyChart = useMemo(() => dailyCosts.map(r => {
        const cogs = Number(r.cogs || 0);
        const opex = Number(r.opex || 0);
        return {
            label: fmtDate(typeof r.sales_date === 'string' ? r.sales_date : (r.sales_date?.toISOString?.() || '')),
            cogs,
            opex,
            total_cost: cogs + opex,
            revenue: Number(r.revenue || 0),
        };
    }), [dailyCosts]);

    const dailyAverage = dailyChart.length > 0
        ? dailyChart.reduce((s, d) => s + d.total_cost, 0) / dailyChart.length
        : 0;
    const spikeThreshold = dailyAverage * 1.2;  // days exceeding 20% above average

    if (loading && !data) {
        return (
            <div style={{ fontFamily: FONT, fontSize: 14, color: C.muted, padding: '60px 0', textAlign: 'center' }}>
                {t('loading')}...
            </div>
        );
    }

    return (
        <div>
            {/* ═══ 1. Header (hidden when parent controls dates) ═══ */}
            {!parentDates && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
                <div>
                    <h2 style={{ fontFamily: FONT, fontSize: 22, fontWeight: 800, color: C.text, margin: 0 }}>
                        {t('cost_management')}
                    </h2>
                    <div style={{ fontFamily: FONT, fontSize: 13, color: C.muted, marginTop: 4 }}>
                        {t('cost_management_subtitle')}
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{
                        display: 'inline-flex', background: C.bg, borderRadius: 8,
                        border: '1.5px solid ' + C.border, overflow: 'hidden',
                    }}>
                        {PRESETS.map(p => (
                            <button
                                key={p.key}
                                onClick={() => setPreset(p.key)}
                                style={{
                                    fontFamily: FONT, fontSize: 12, fontWeight: 600,
                                    padding: '7px 14px', border: 'none', cursor: 'pointer',
                                    background: preset === p.key ? C.green : 'transparent',
                                    color: preset === p.key ? C.white : C.muted,
                                    transition: 'all 0.15s',
                                }}
                            >{t(p.label)}</button>
                        ))}
                    </div>
                    {preset === 'custom' && (
                        <>
                            <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)}
                                style={{ fontFamily: FONT, fontSize: 12, padding: '6px 10px', border: '1.5px solid ' + C.border, borderRadius: 8, outline: 'none' }} />
                            <span style={{ fontFamily: FONT, fontSize: 12, color: C.muted }}>{t('to')}</span>
                            <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)}
                                style={{ fontFamily: FONT, fontSize: 12, padding: '6px 10px', border: '1.5px solid ' + C.border, borderRadius: 8, outline: 'none' }} />
                        </>
                    )}
                    <button
                        onClick={() => window.print()}
                        style={{
                            fontFamily: FONT, fontSize: 12, fontWeight: 700,
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                            padding: '8px 16px', border: '1.5px solid ' + C.border, borderRadius: 8,
                            background: C.white, color: C.text, cursor: 'pointer', transition: 'all 0.15s',
                            whiteSpace: 'nowrap',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = C.bg; }}
                        onMouseLeave={e => { e.currentTarget.style.background = C.white; }}
                    >
                        <DownloadIcon /> {t('export_pdf')}
                    </button>
                </div>
            </div>

            )}

            {/* ═══ 2. AI Narrative Card ═══ */}
            <SectionCard style={{
                background: 'linear-gradient(135deg, #eff5ff 0%, #ffffff 100%)',
                border: '1.5px solid ' + C.greenBorder,
                borderRadius: 14,
                marginBottom: 20,
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={C.green} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" />
                    </svg>
                    <span style={{ fontFamily: FONT, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: C.greenDark }}>
                        {t('ai_cost_briefing')}
                    </span>
                </div>
                <div style={{ fontFamily: FONT, fontSize: 15, fontWeight: 500, color: C.text, lineHeight: 1.6 }}
                    dangerouslySetInnerHTML={{
                        __html: narrative?.narrative
                            ? narrative.narrative.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                            : t('cost_briefing_fallback')
                    }}
                />
                <div style={{ fontFamily: FONT, fontSize: 10, color: C.mutedLight, marginTop: 12 }}>
                    {t('auto_generated')}
                    {narrative?.generated_at && ` · ${t('updated')} ${(() => {
                        const diff = Math.round((Date.now() - new Date(narrative.generated_at).getTime()) / 3600000);
                        return diff < 1 ? t('just_now') : `${diff}h ${t('ago')}`;
                    })()}`}
                    {` · ${currentLabel}`}
                </div>
            </SectionCard>

            {/* ═══ 3. Cost Data Health ═══ */}
            {health.total_revenue > 0 && health.trusted_revenue_pct < 100 && (
                <SectionCard style={{
                    marginBottom: 20,
                    background: C.amberLight,
                    border: '1.5px solid ' + C.amberBorder,
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, gap: 12, flexWrap: 'wrap' }}>
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                <div style={{ color: C.amber }}><WarningIcon /></div>
                                <div style={{ fontFamily: FONT, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: C.amber }}>
                                    {t('cost_data_health')}
                                </div>
                            </div>
                            <div style={{ fontFamily: FONT, fontSize: 15, fontWeight: 700, color: C.text }}>
                                {t('trusted_revenue_pct').replace('{pct}', `${health.trusted_revenue_pct.toFixed(1)}%`)}
                            </div>
                            <div style={{ fontFamily: FONT, fontSize: 12, color: C.muted, marginTop: 4 }}>
                                {t('untrusted_revenue_note').replace('{amount}', fmt(health.untrusted_revenue, currency))}
                            </div>
                        </div>
                        {health.flagged_products?.length > 0 && (
                            <button
                                onClick={() => setHealthExpanded(!healthExpanded)}
                                style={{
                                    fontFamily: FONT, fontSize: 12, fontWeight: 700,
                                    padding: '8px 14px', border: '1.5px solid ' + C.amber,
                                    background: C.white, color: C.amber, cursor: 'pointer',
                                    borderRadius: 8, whiteSpace: 'nowrap',
                                }}
                            >
                                {healthExpanded ? t('hide_flagged_products') : t('show_flagged_products').replace('{n}', health.flagged_products.length)}
                            </button>
                        )}
                    </div>
                    {healthExpanded && health.flagged_products && (
                        <>
                            <div style={{ background: C.white, borderRadius: 8, border: '1px solid ' + C.amberBorder, marginTop: 8 }}>
                                {health.flagged_products.map((p, i) => (
                                    <div
                                        key={p.product_code}
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => openProductFix(p)}
                                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openProductFix(p); } }}
                                        onMouseEnter={(e) => { e.currentTarget.style.background = C.bg; }}
                                        onMouseLeave={(e) => { e.currentTarget.style.background = C.white; }}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: 12,
                                            padding: '10px 14px',
                                            borderBottom: i < health.flagged_products.length - 1 ? '1px solid ' + C.border : 'none',
                                            cursor: 'pointer',
                                            transition: 'background 0.12s',
                                            outline: 'none',
                                        }}
                                        title={p.cost_source === 'recipe_partial' ? t('click_to_fix_recipe') : t('click_to_fix_cost')}
                                    >
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ fontFamily: FONT, fontSize: 13, fontWeight: 600, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {p.product_name || p.product_code}
                                            </div>
                                            <div style={{ fontFamily: FONT, fontSize: 11, color: C.muted, marginTop: 2 }}>
                                                {p.cost_source === 'unknown' ? t('cost_source_unknown') : t('cost_source_recipe_partial')}
                                            </div>
                                        </div>
                                        <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                                            <div style={{ fontFamily: FONT, fontSize: 13, fontWeight: 700, color: C.text }}>{fmt(p.revenue, currency)}</div>
                                            <div style={{ fontFamily: FONT, fontSize: 11, color: C.muted }}>{p.units} {t('units')}</div>
                                        </div>
                                        <div style={{
                                            fontFamily: FONT, fontSize: 11, fontWeight: 700,
                                            color: C.amber, whiteSpace: 'nowrap',
                                        }}>
                                            {p.cost_source === 'recipe_partial' ? t('fix_recipe') : t('fix_cost')} →
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Collapse affordance at the bottom — users don't have to scroll
                                back up to hide the list once they're done scanning it. */}
                            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12 }}>
                                <button
                                    onClick={() => setHealthExpanded(false)}
                                    style={{
                                        fontFamily: FONT, fontSize: 12, fontWeight: 700,
                                        padding: '7px 14px',
                                        border: '1.5px solid ' + C.amber,
                                        background: C.amberLight, color: C.amber,
                                        cursor: 'pointer', borderRadius: 8,
                                    }}
                                >
                                    ↑ {t('hide_flagged_products')}
                                </button>
                            </div>
                        </>
                    )}
                </SectionCard>
            )}

            {/* ═══ 4. KPI Cards ═══ */}
            {isMobile ? (kpis.length > 0 ? (
                <MobileHero
                    label={kpis[0]?.name}
                    value={(/%/.test(kpis[0]?.name || '')) ? Number(kpis[0].value || 0).toFixed(1) + '%' : fmt(kpis[0]?.value, currency)}
                    meta={`${(kpis[0]?.change_percent || 0) >= 0 ? '↑' : '↓'} ${Math.abs(kpis[0]?.change_percent || 0).toFixed(1)}% ${currentLabel}`}
                    stats={kpis.slice(1, 5).map(k => ({
                        label: k.name,
                        value: (/%/.test(k.name || '')) ? Number(k.value || 0).toFixed(1) + '%' : fmt(k.value, currency),
                    }))}
                />
            ) : null) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: 14, marginBottom: 28 }}>
                {kpis.map((kpi, idx) => {
                    const isHero = idx === 0;
                    const flipped = flippedCards[idx];
                    const positive = kpi.invert ? (kpi.change_percent || 0) <= 0 : (kpi.change_percent || 0) >= 0;
                    const changeColor = positive ? C.green : C.red;
                    const arrow = (kpi.change_percent || 0) >= 0 ? '↑' : '↓';
                    const isPct = /%/.test(kpi.name || '');
                    const descKey = (kpi.name || '').toLowerCase().replace(/\s+/g, '_').replace(/%/g, '');
                    const description = kpiDescriptions[descKey]
                        || kpiDescriptions[kpi.name]
                        || kpiDescriptions[(kpi.name || '').toLowerCase()]
                        || t('no_description_available');
                    const cardHeight = 125;

                    return (
                        <div key={kpi.name || idx} style={{ perspective: 1000, height: cardHeight }}>
                            <div style={{
                                transition: 'transform 0.3s ease-out',
                                transformStyle: 'preserve-3d',
                                transform: flipped ? 'rotateY(180deg)' : 'rotateY(0)',
                                position: 'relative', width: '100%', height: '100%',
                            }}>
                                {/* Front */}
                                <div style={{
                                    backfaceVisibility: 'hidden', position: 'absolute', width: '100%', height: '100%',
                                    background: isHero ? C.greenLight : C.white,
                                    border: '1.5px solid ' + (isHero ? C.greenBorder : C.border),
                                    borderRadius: 12, padding: '12px 12px',
                                    display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
                                    boxSizing: 'border-box', overflow: 'hidden',
                                }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 4 }}>
                                        <div style={{
                                            fontFamily: FONT, fontSize: 10, fontWeight: 700,
                                            textTransform: 'uppercase', letterSpacing: 0.8,
                                            color: C.muted,
                                        }}>
                                            {kpi.name}
                                        </div>
                                        <button
                                            onClick={() => toggleFlip(idx)}
                                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: C.mutedLight, opacity: 0.7, flexShrink: 0 }}
                                            title={t('more_info')}
                                        >
                                            <InfoIcon />
                                        </button>
                                    </div>
                                    <div>
                                        <div style={{
                                            fontFamily: FONT, fontSize: 24, fontWeight: 800, color: C.text,
                                            lineHeight: 1.2, marginBottom: 4,
                                        }}>
                                            {isPct ? `${Number(kpi.value || 0).toFixed(1)}%` : fmt(kpi.value, currency)}
                                        </div>
                                        {kpi.change_percent != null ? (
                                            <div style={{ fontFamily: FONT, fontSize: 12, fontWeight: 600, color: changeColor }}>
                                                {arrow} {Math.abs(kpi.change_percent).toFixed(1)}%
                                            </div>
                                        ) : (
                                            <div style={{ fontFamily: FONT, fontSize: 12, color: C.mutedLight }}>—</div>
                                        )}
                                    </div>
                                    {kpi.change_absolute != null && (
                                        <div style={{ fontFamily: FONT, fontSize: 11, color: C.muted }}>
                                            {isPct
                                                ? `${kpi.previous_value != null ? Number(kpi.previous_value).toFixed(1) + '% prev' : ''}`
                                                : `${kpi.change_absolute >= 0 ? '+' : ''}${fmt(kpi.change_absolute, currency)}`
                                            }
                                        </div>
                                    )}
                                    <div style={{ fontFamily: FONT, fontSize: 10, color: C.mutedLight }}>
                                        {prevLabel ? `vs ${prevLabel}` : currentLabel}
                                    </div>
                                </div>

                                {/* Back */}
                                <div style={{
                                    backfaceVisibility: 'hidden', transform: 'rotateY(180deg)',
                                    position: 'absolute', width: '100%', height: '100%',
                                    background: C.white, border: '1.5px solid ' + C.border,
                                    borderRadius: 12, padding: '16px 18px',
                                    display: 'flex', flexDirection: 'column', justifyContent: 'center',
                                    boxSizing: 'border-box',
                                }}>
                                    <div style={{ fontFamily: FONT, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: C.green, marginBottom: 8 }}>
                                        {kpi.name}
                                    </div>
                                    <div style={{ fontFamily: FONT, fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
                                        {description}
                                    </div>
                                    <button
                                        onClick={() => toggleFlip(idx)}
                                        style={{ fontFamily: FONT, fontSize: 11, fontWeight: 600, color: C.green, background: 'none', border: 'none', cursor: 'pointer', marginTop: 8, textAlign: 'left', padding: 0 }}
                                    >
                                        ← {t('back')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
            )}

            {/* ═══ 5. P&L Cost Flow ═══ */}
            <SectionCard style={{ marginBottom: 28 }}>
                <div style={sectionLabel}>{t('cost_flow')}</div>
                <div style={{ fontFamily: FONT, fontSize: 12, color: C.muted, marginTop: -12, marginBottom: 20 }}>
                    {t('cost_flow_description')}
                </div>
                {flow.revenue > 0 ? (
                    <CostFlow flow={flow} currency={currency} t={t} />
                ) : (
                    <EmptyState title={t('no_data_in_period')} />
                )}
            </SectionCard>

            {/* ═══ 6. Section Divider: Deep Dive ═══ */}
            <SectionDivider label={t('deep_dive')} tag={t('explorer')} />

            {/* ═══ 7. Operating Expense Breakdown ═══ */}
            <div className="portal-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 28 }}>
                <SectionCard>
                    <div style={sectionLabel}>{t('overhead_expenses')}</div>
                    <div style={{ fontFamily: FONT, fontSize: 12, color: C.muted, marginTop: -12, marginBottom: 16 }}>
                        {t('overhead_description')}
                    </div>
                    {overhead.length > 0 ? (
                        <>
                            <div style={{ fontFamily: FONT, fontSize: 11, color: C.muted, marginBottom: 10 }}>
                                {t('total')}: {fmt(overheadTotal, currency)}
                            </div>
                            {isMobile ? (
                                <div>
                                    {overhead.map((e) => (
                                        <MobileCard
                                            key={e.category}
                                            accent="amber"
                                            title={e.category}
                                            amount={fmt(Number(e.total_cost), currency)}
                                            subtitle={`${(overheadTotal > 0 ? (Number(e.total_cost) / overheadTotal) * 100 : 0).toFixed(1)}% ${t('of_total') || 'of total'}`}
                                            collapsible={false}
                                        />
                                    ))}
                                </div>
                            ) : (
                                <>
                                    {overhead.map((e) => (
                                        <BarListItem
                                            key={e.category}
                                            label={e.category}
                                            value={Number(e.total_cost)}
                                            maxValue={overheadMax}
                                            color={C.amber}
                                            cur={currency}
                                            pct={overheadTotal > 0 ? (Number(e.total_cost) / overheadTotal) * 100 : 0}
                                        />
                                    ))}
                                </>
                            )}
                        </>
                    ) : (
                        <EmptyState title={t('no_overhead_in_period')} />
                    )}
                </SectionCard>

                <SectionCard>
                    <div style={sectionLabel}>{t('daily_expenses')}</div>
                    <div style={{ fontFamily: FONT, fontSize: 12, color: C.muted, marginTop: -12, marginBottom: 16 }}>
                        {t('daily_expenses_description')}
                    </div>
                    {daily.length > 0 ? (
                        <>
                            <div style={{ fontFamily: FONT, fontSize: 11, color: C.muted, marginBottom: 10 }}>
                                {t('total')}: {fmt(dailyTotal, currency)}
                            </div>
                            {isMobile ? (
                                <div>
                                    {daily.map((e) => (
                                        <MobileCard
                                            key={e.category}
                                            accent="green"
                                            title={e.category}
                                            amount={fmt(Number(e.total_cost), currency)}
                                            subtitle={`${(dailyTotal > 0 ? (Number(e.total_cost) / dailyTotal) * 100 : 0).toFixed(1)}% ${t('of_total') || 'of total'}`}
                                            collapsible={false}
                                        />
                                    ))}
                                </div>
                            ) : (
                                <>
                                    {daily.map((e) => (
                                        <BarListItem
                                            key={e.category}
                                            label={e.category}
                                            value={Number(e.total_cost)}
                                            maxValue={dailyMax}
                                            color={C.blue}
                                            cur={currency}
                                            pct={dailyTotal > 0 ? (Number(e.total_cost) / dailyTotal) * 100 : 0}
                                        />
                                    ))}
                                </>
                            )}
                        </>
                    ) : (
                        <EmptyState title={t('no_daily_expenses_in_period')} />
                    )}
                </SectionCard>
            </div>

            {/* ═══ 8. Price Variance Alerts ═══ */}
            {priceVariance.length > 0 && (
                <SectionCard style={{ marginBottom: 28 }}>
                    <div style={sectionLabel}>{t('price_variance_alerts')}</div>
                    <div style={{ fontFamily: FONT, fontSize: 12, color: C.muted, marginTop: -12, marginBottom: 16 }}>
                        {t('price_variance_description')}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {priceVariance.map((v) => {
                            const up = v.delta_pct >= 0;
                            const impactColor = v.impact >= 0 ? C.red : C.green;
                            return (
                                <div key={v.inventory_id} style={{
                                    border: '1px solid ' + C.border, borderRadius: 10,
                                    padding: '14px 16px', background: C.white,
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                                        <div style={{
                                            fontFamily: FONT, fontSize: 14, fontWeight: 700, color: C.text,
                                            flex: 1, minWidth: 0,
                                        }}>
                                            {v.name}
                                        </div>
                                        <div style={{
                                            fontFamily: FONT, fontSize: 12, fontWeight: 700,
                                            padding: '3px 10px', borderRadius: 6,
                                            background: up ? C.redLight : C.greenLight,
                                            color: up ? C.red : C.greenDark,
                                        }}>
                                            {up ? '↑' : '↓'} {Math.abs(v.delta_pct).toFixed(1)}%
                                        </div>
                                        <div style={{
                                            fontFamily: FONT, fontSize: 13, fontWeight: 700,
                                            color: impactColor, whiteSpace: 'nowrap',
                                        }}>
                                            {v.impact >= 0 ? '+' : ''}{fmt(v.impact, currency)}
                                        </div>
                                    </div>
                                    <div style={{ fontFamily: FONT, fontSize: 11, color: C.muted, marginTop: 8 }}>
                                        {fmt(v.prev_avg_cost, currency)} → {fmt(v.curr_avg_cost, currency)} · {t('units_consumed').replace('{n}', Number(v.units_consumed || 0).toLocaleString())}
                                    </div>
                                    {v.affected_products?.length > 0 && (
                                        <div style={{ fontFamily: FONT, fontSize: 11, color: C.mutedLight, marginTop: 6 }}>
                                            {t('affected_products')}: {v.affected_products.join(', ')}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </SectionCard>
            )}

            {/* ═══ 9. Daily Cost Patterns ═══ */}
            <SectionCard style={{ marginBottom: 28 }}>
                <div style={sectionLabel}>{t('daily_cost_patterns')}</div>
                <div style={{ fontFamily: FONT, fontSize: 12, color: C.muted, marginTop: -12, marginBottom: 16 }}>
                    {t('daily_cost_patterns_description')}
                </div>
                {dailyChart.length > 0 ? (
                    <ResponsiveContainer width="100%" height={260}>
                        <AreaChart data={dailyChart} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                            <defs>
                                <linearGradient id="cogsGradient" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor={C.green} stopOpacity={0.35} />
                                    <stop offset="100%" stopColor={C.green} stopOpacity={0.05} />
                                </linearGradient>
                                <linearGradient id="opexGradient" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor={C.amber} stopOpacity={0.35} />
                                    <stop offset="100%" stopColor={C.amber} stopOpacity={0.05} />
                                </linearGradient>
                            </defs>
                            <XAxis dataKey="label" tick={{ fontFamily: FONT, fontSize: 10, fill: C.muted }} axisLine={false} tickLine={false} />
                            <YAxis tick={{ fontFamily: FONT, fontSize: 10, fill: C.muted }} axisLine={false} tickLine={false} />
                            <Tooltip
                                contentStyle={{ fontFamily: FONT, fontSize: 12, borderRadius: 8, border: '1px solid ' + C.border }}
                                formatter={(v, name) => [fmt(v, currency), t(name)]}
                            />
                            {spikeThreshold > 0 && (
                                <ReferenceLine y={spikeThreshold} stroke={C.red} strokeDasharray="4 4" label={{
                                    value: `${t('spike_threshold')} · ${fmt(spikeThreshold, currency)}`,
                                    position: 'insideTopRight',
                                    fill: C.red,
                                    fontFamily: FONT,
                                    fontSize: 10,
                                }} />
                            )}
                            <Area type="monotone" dataKey="cogs" stackId="1" stroke={C.green} fill="url(#cogsGradient)" name="cogs" />
                            <Area type="monotone" dataKey="opex" stackId="1" stroke={C.amber} fill="url(#opexGradient)" name="opex" />
                        </AreaChart>
                    </ResponsiveContainer>
                ) : (
                    <EmptyState title={t('no_data_in_period')} />
                )}
                <div style={{ display: 'flex', justifyContent: 'center', gap: 18, marginTop: 12 }}>
                    <LegendDot color={C.green} label={t('cogs')} />
                    <LegendDot color={C.amber} label={t('opex')} />
                    <LegendDot color={C.red} label={t('spike_threshold')} dashed />
                </div>
            </SectionCard>

            {/* ═══ 10. Insights & Actions ═══ */}
            {insights.length > 0 && (
                <SectionCard>
                    <div style={sectionLabel}>{t('insights_and_actions')}</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 4 }}>
                        {insights.map((ins, i) => {
                            const palette = {
                                warning: { bg: C.amberLight, border: C.amberBorder, color: C.amber, Icon: WarningIcon },
                                opportunity: { bg: C.blueLight, border: C.blueBorder, color: C.blue, Icon: TrendUpIcon },
                                success: { bg: C.greenLight, border: C.greenBorder, color: C.green, Icon: TrendUpIcon },
                                info: { bg: C.blueLight, border: C.blueBorder, color: C.blue, Icon: InfoIcon },
                            };
                            const style = palette[ins.type] || palette.info;
                            const Icon = style.Icon;
                            return (
                                <div key={i} style={{
                                    display: 'flex', alignItems: 'flex-start', gap: 12,
                                    padding: '12px 14px', borderRadius: 10,
                                    background: style.bg, border: '1px solid ' + style.border,
                                }}>
                                    <div style={{ color: style.color, flexShrink: 0, marginTop: 2 }}>
                                        <Icon />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontFamily: FONT, fontSize: 13, fontWeight: 700, color: C.text }}>
                                            {ins.title}
                                        </div>
                                        <div style={{ fontFamily: FONT, fontSize: 12, color: C.muted, marginTop: 4, lineHeight: 1.5 }}>
                                            {ins.description}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </SectionCard>
            )}
        </div>
    );
}


/* ══════════════════════════════════════════════════════════════
   Cost Flow — horizontal decomposition Revenue → COGS → Gross Profit → OpEx → Net
   ══════════════════════════════════════════════════════════════ */

function CostFlow({ flow, currency, t }) {
    const revenue = Math.max(flow.revenue || 0, 1);
    const pct = (v) => (v / revenue) * 100;

    const segs = [
        { key: 'revenue', label: t('revenue'), amount: flow.revenue, pct: 100, color: C.green, dark: C.greenDark },
        { key: 'cogs', label: t('cogs'), amount: flow.cogs, pct: pct(flow.cogs), color: C.amber, dark: C.amber },
        { key: 'gross_profit', label: t('gross_profit'), amount: flow.gross_profit, pct: pct(flow.gross_profit), color: C.blue, dark: C.blue },
        { key: 'opex', label: t('operating_expenses'), amount: flow.opex, pct: pct(flow.opex), color: C.purple, dark: C.purple },
        { key: 'net_profit', label: t('net_profit'), amount: flow.net_profit, pct: pct(flow.net_profit), color: flow.net_profit >= 0 ? C.greenDark : C.red, dark: flow.net_profit >= 0 ? C.greenDark : C.red },
    ];

    return (
        <div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {segs.map((s) => {
                    const width = Math.max(2, Math.min(100, Math.abs(s.pct)));
                    const isNegative = s.amount < 0;
                    return (
                        <div key={s.key}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                                <div style={{ fontFamily: FONT, fontSize: 13, fontWeight: 700, color: C.text }}>{s.label}</div>
                                <div style={{ fontFamily: FONT, fontSize: 13, fontWeight: 700, color: isNegative ? C.red : C.text }}>
                                    {fmt(s.amount, currency)} <span style={{ color: C.muted, fontWeight: 500 }}>({s.pct.toFixed(1)}%)</span>
                                </div>
                            </div>
                            <div style={{ height: 14, background: C.bg, borderRadius: 6, overflow: 'hidden', border: '1px solid ' + C.border }}>
                                <div style={{
                                    width: width + '%',
                                    height: '100%',
                                    background: isNegative
                                        ? `repeating-linear-gradient(45deg, ${C.redLight}, ${C.redLight} 6px, ${C.white} 6px, ${C.white} 12px)`
                                        : s.color,
                                    borderRight: '1px solid ' + s.dark,
                                    transition: 'width 0.4s ease',
                                }} />
                            </div>
                        </div>
                    );
                })}
            </div>

            <div style={{
                marginTop: 24, padding: 16, borderRadius: 10,
                background: C.bg, border: '1px dashed ' + C.border,
                fontFamily: FONT, fontSize: 13, color: C.text, lineHeight: 1.6,
            }}>
                {t('cost_flow_narrative')
                    .replace('{cogs_pct}', (flow.cogs_pct || 0).toFixed(1))
                    .replace('{opex_pct}', (flow.opex_pct || 0).toFixed(1))
                    .replace('{net_margin_pct}', (flow.net_margin_pct || 0).toFixed(1))}
            </div>
        </div>
    );
}

function LegendDot({ color, label, dashed }) {
    return (
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span style={{
                display: 'inline-block',
                width: 20, height: 3, borderRadius: 2,
                background: dashed
                    ? `repeating-linear-gradient(90deg, ${color}, ${color} 4px, transparent 4px, transparent 8px)`
                    : color,
            }} />
            <span style={{ fontFamily: FONT, fontSize: 11, color: C.muted, fontWeight: 600 }}>
                {label}
            </span>
        </div>
    );
}
